/**
 * @fileoverview Component for displaying a list of staff members.
 * This component includes a search bar, filters, and pagination.
 */

import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGetStaffCountQuery, useGetStaffQuery } from '../../services/staff'
import { Search } from '../elements/Search'
import { Pagination } from '../Pagination'
import { StaffListEmpty } from './StaffListEmpty'
import { StaffListTable } from './StaffListTable'

/**
 * @function StaffList
 * @description Component for displaying a list of staff members.
 * @returns {JSX.Element} - A React component that renders the list of staff members.
 */
export function StaffList() {
    const navigate = useNavigate()

    const { data: staff, isLoading, refetch: refetchStaff } = useGetStaffQuery()
    const { data: total, refetch: refetchStaffCount } = useGetStaffCountQuery()

    useEffect(() => {
        refetchStaff()
        refetchStaffCount()
    }, [location.search])

    function handleRowClick(e: Event, userId: string) {
        if (e.target instanceof HTMLInputElement) {
            return
        }
        navigate(userId + location.search)
    }

    if (isLoading) {
        return null
    }

    return (
        <>
            <div className="flex flex-wrap fixed top-0 left-[256px] right-0 p-8 bg-white z-20 border-b justify-between">
                <div className="flex flex-col gap-y-6">
                    <div className="flex items-center h-full">
                        <span className="text-2xl font-semibold mr-3">Staff</span>
                    </div>
                    <Search placeholder="Search by staff name or email..." />
                </div>
            </div>
            <div className="relative w-full mt-[161px]">
                <div className="flex flex-wrap bg-white">
                    <div className="w-full min-h-[calc(100vh_-_204px)] flex flex-wrap">
                        <div className="inline-block min-w-full align-middle">
                            {total?.count > 0 ? (
                                <div className="flex flex-col">
                                    <StaffListTable data={staff} onRowClicked={handleRowClick} />
                                    <div className="flex w-full h-[74px] items-center bg-white border-t border-t-gray-200">
                                        <Pagination total={total?.count} />
                                    </div>
                                </div>
                            ) : (
                                <StaffListEmpty />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}
